import Client from 'clients/base/Client'
import { useCore } from 'contexts/core-context'
import { useGlobal } from 'contexts/global-context'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from 'ui-toolkit-tailwind/src/components'
import { showToast } from 'utilities/showToast'

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const emptyStateCopy = {
	owners: 'No all-location owners have been configured yet.',
	admins: 'No admin accounts have been configured yet.'
}

const describeError = responseJson =>
	responseJson?.message || responseJson?.errors?.email?.[0] || 'Unable to save access changes.'

const formatLastLogin = value => {
	if (!value) return 'Never'

	const parsed = dayjs(value)
	if (!parsed.isValid()) return 'Never'

	return parsed.format('M/D/YY h:mm A')
}

const AccessRow = ({
	entry,
	canRemove,
	removing,
	onRemove
}) => (
	<div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
		<div className="min-w-0 space-y-1">
			<div className="flex flex-wrap items-center gap-2">
				<span className="truncate text-sm font-semibold text-slate-900">{entry.email}</span>
				<span
					className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
						entry.hasAccount
							? 'bg-emerald-50 text-emerald-700'
							: 'bg-amber-50 text-amber-700'
					}`}
				>
					{entry.hasAccount ? 'Account Ready' : 'Invite Pending'}
				</span>
			</div>
			<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
				<span>{entry.name || 'No profile name yet'}</span>
				<span>Last login: {formatLastLogin(entry.dateLastLogin)}</span>
			</div>
		</div>

		<Button
			type="button"
			intent="secondary"
			size="sm"
			disabled={!canRemove || removing}
			onClick={onRemove}
			className="sm:min-w-[7rem]"
		>
			{removing ? 'Removing...' : 'Remove'}
		</Button>
	</div>
)

AccessRow.propTypes = {
	entry: PropTypes.shape({
		email: PropTypes.string.isRequired,
		hasAccount: PropTypes.bool,
		name: PropTypes.string,
		dateLastLogin: PropTypes.string
	}).isRequired,
	canRemove: PropTypes.bool,
	removing: PropTypes.bool,
	onRemove: PropTypes.func.isRequired
}

AccessRow.defaultProps = {
	canRemove: true,
	removing: false
}

const AccessSection = ({
	title,
	description,
	entries,
	emptyState,
	emailValue,
	emailPlaceholder,
	submitLabel,
	submitting,
	disabled,
	onEmailChange,
	onSubmit,
	renderRemoveDisabledReason,
	onRemove,
	rowIsRemoving
}) => (
	<section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
		<div className="border-b border-slate-200 px-4 py-4 sm:px-6">
			<h2 className="text-base font-semibold text-slate-900">{title}</h2>
			<p className="mt-1 text-sm text-slate-600">{description}</p>
		</div>

		<div className="border-b border-slate-200 px-4 py-4 sm:px-6">
			<form className="flex flex-col gap-3 sm:flex-row" onSubmit={onSubmit}>
				<input
					type="email"
					value={emailValue}
					onChange={event => onEmailChange(event.target.value)}
					placeholder={emailPlaceholder}
					disabled={disabled}
					className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-[#3E63F4] focus:ring-2 focus:ring-[#3E63F4]/20"
				/>
				<Button type="submit" disabled={disabled || submitting} className="sm:min-w-[9rem]">
					{submitting ? 'Saving...' : submitLabel}
				</Button>
			</form>
		</div>

		<div>
			{entries.length === 0 ? (
				<div className="px-4 py-6 text-sm text-slate-500 sm:px-6">{emptyState}</div>
			) : (
				entries.map(entry => {
					const removeDisabledReason = renderRemoveDisabledReason(entry)

					return (
						<div key={entry.email} title={removeDisabledReason || undefined}>
							<AccessRow
								entry={entry}
								canRemove={!removeDisabledReason}
								removing={rowIsRemoving(entry)}
								onRemove={() => onRemove(entry)}
							/>
						</div>
					)
				})
			)}
		</div>
	</section>
)

AccessSection.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	entries: PropTypes.arrayOf(PropTypes.object).isRequired,
	emptyState: PropTypes.string.isRequired,
	emailValue: PropTypes.string.isRequired,
	emailPlaceholder: PropTypes.string.isRequired,
	submitLabel: PropTypes.string.isRequired,
	submitting: PropTypes.bool,
	disabled: PropTypes.bool,
	onEmailChange: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	renderRemoveDisabledReason: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	rowIsRemoving: PropTypes.func.isRequired
}

AccessSection.defaultProps = {
	submitting: false,
	disabled: false
}

const AccessManagement = () => {
	const { executeLogout, refreshSession } = useCore()
	const { setShowSpinner } = useGlobal()
	const [accessState, setAccessState] = useState({
		currentUserEmail: null,
		owners: [],
		admins: []
	})
	const [ownerEmail, setOwnerEmail] = useState('')
	const [adminEmail, setAdminEmail] = useState('')
	const [activeMutation, setActiveMutation] = useState(null)

	const applyPayload = useCallback(payload => {
		setAccessState({
			currentUserEmail: payload?.currentUserEmail || null,
			owners: payload?.owners || [],
			admins: payload?.admins || []
		})
	}, [])

	const apiActivityOptions = useMemo(() => ({
		onApiCall: () => setShowSpinner(true),
		onApiResponse: () => setShowSpinner(false)
	}), [setShowSpinner])

	const loadAccessManagement = useCallback(() => {
		Client.OwnerAdminApi.accessManagement(
			{
				status200: response => {
					applyPayload(response)
				},
				status403: () => {
					showToast({
						intent: 'error',
						title: 'Access Management',
						content: 'Not authorized to manage access.'
					})
					executeLogout()
				},
				error: error => {
					console.error(error)
					showToast({
						intent: 'error',
						title: 'Access Management',
						content: 'Unable to load access settings.'
					})
				}
			},
			apiActivityOptions
		)
	}, [apiActivityOptions, applyPayload, executeLogout])

	useEffect(() => {
		loadAccessManagement()
	}, [loadAccessManagement])

	const handleMutationSuccess = useCallback((title, content, payload) => {
		applyPayload(payload)
		refreshSession()
		showToast({
			intent: 'success',
			title,
			content
		})
	}, [applyPayload, refreshSession])

	const handleMutationFailure = useCallback((title, content) => {
		showToast({
			intent: 'error',
			title,
			content
		})
	}, [])

	const submitOwner = event => {
		event.preventDefault()

		const normalizedEmail = ownerEmail.trim().toLowerCase()
		if (!emailPattern.test(normalizedEmail)) {
			handleMutationFailure('Add Owner', 'Enter a valid email address.')
			return
		}

		setActiveMutation('add-owner')
		Client.OwnerAdminApi.addOwnerAccess(
			{ email: normalizedEmail },
			{
				status201: response => {
					setOwnerEmail('')
					handleMutationSuccess(
						'Owner Added',
						response?.invitationQueued
							? `${normalizedEmail} can now view all Gaming Productions locations. A registration email has been sent.`
							: `${normalizedEmail} can now view all Gaming Productions locations.`,
						response
					)
				},
				status403: () => {
					handleMutationFailure('Add Owner', 'Not authorized to manage access.')
					executeLogout()
				},
				status409: responseJson => {
					handleMutationFailure('Add Owner', describeError(responseJson))
				},
				status422: responseJson => {
					handleMutationFailure('Add Owner', describeError(responseJson))
				},
				error: error => {
					console.error(error)
					handleMutationFailure('Add Owner', 'Unable to add owner access.')
				}
			},
			{
				...apiActivityOptions,
				onApiResponse: () => {
					setShowSpinner(false)
					setActiveMutation(null)
				}
			}
		)
	}

	const submitAdmin = event => {
		event.preventDefault()

		const normalizedEmail = adminEmail.trim().toLowerCase()
		if (!emailPattern.test(normalizedEmail)) {
			handleMutationFailure('Add Admin', 'Enter a valid email address.')
			return
		}

		setActiveMutation('add-admin')
		Client.OwnerAdminApi.addAdminAccess(
			{ email: normalizedEmail },
			{
				status201: response => {
					setAdminEmail('')
					handleMutationSuccess(
						'Admin Added',
						response?.invitationQueued
							? `${normalizedEmail} can now manage owners, admins, and manual actions. A registration email has been sent.`
							: `${normalizedEmail} can now manage owners, admins, and manual actions.`,
						response
					)
				},
				status403: () => {
					handleMutationFailure('Add Admin', 'Not authorized to manage access.')
					executeLogout()
				},
				status409: responseJson => {
					handleMutationFailure('Add Admin', describeError(responseJson))
				},
				status422: responseJson => {
					handleMutationFailure('Add Admin', describeError(responseJson))
				},
				error: error => {
					console.error(error)
					handleMutationFailure('Add Admin', 'Unable to add admin access.')
				}
			},
			{
				...apiActivityOptions,
				onApiResponse: () => {
					setShowSpinner(false)
					setActiveMutation(null)
				}
			}
		)
	}

	const removeOwner = entry => {
		setActiveMutation(`remove-owner:${entry.email}`)
		Client.OwnerAdminApi.removeOwnerAccess(
			entry.email,
			{
				status200: response => {
					handleMutationSuccess(
						'Owner Removed',
						`${entry.email} no longer has all-location owner access.`,
						response
					)
				},
				status403: () => {
					handleMutationFailure('Remove Owner', 'Not authorized to manage access.')
					executeLogout()
				},
				status409: responseJson => {
					handleMutationFailure('Remove Owner', describeError(responseJson))
				},
				status422: responseJson => {
					handleMutationFailure('Remove Owner', describeError(responseJson))
				},
				error: error => {
					console.error(error)
					handleMutationFailure('Remove Owner', 'Unable to remove owner access.')
				}
			},
			{
				...apiActivityOptions,
				onApiResponse: () => {
					setShowSpinner(false)
					setActiveMutation(null)
				}
			}
		)
	}

	const removeAdmin = entry => {
		setActiveMutation(`remove-admin:${entry.email}`)
		Client.OwnerAdminApi.removeAdminAccess(
			entry.email,
			{
				status200: response => {
					handleMutationSuccess(
						'Admin Removed',
						`${entry.email} no longer has admin access.`,
						response
					)
				},
				status403: () => {
					handleMutationFailure('Remove Admin', 'Not authorized to manage access.')
					executeLogout()
				},
				status409: responseJson => {
					handleMutationFailure('Remove Admin', describeError(responseJson))
				},
				status422: responseJson => {
					handleMutationFailure('Remove Admin', describeError(responseJson))
				},
				error: error => {
					console.error(error)
					handleMutationFailure('Remove Admin', 'Unable to remove admin access.')
				}
			},
			{
				...apiActivityOptions,
				onApiResponse: () => {
					setShowSpinner(false)
					setActiveMutation(null)
				}
			}
		)
	}

	const adminCount = accessState.admins.length
	const actionsLocked = activeMutation !== null

	const renderAdminRemoveDisabledReason = entry => {
		if (actionsLocked) {
			return 'Please wait for the current change to finish.'
		}

		if (entry.email === accessState.currentUserEmail) {
			return 'You cannot remove your own admin access.'
		}

		if (adminCount <= 1) {
			return 'At least one admin account must remain.'
		}

		return null
	}

	return (
		<div className="space-y-6">
			<div className="max-w-3xl space-y-2">
				<h1 className="text-xl font-semibold text-slate-900">Access Management</h1>
				<p className="text-sm text-slate-600">
					Manage the all-location Gaming Productions management list and the admin accounts that
					can access operational controls. Changes are saved immediately and apply from the
					database.
				</p>
			</div>

			<div className="grid gap-6 xl:grid-cols-2">
				<AccessSection
					title="Gaming Productions Management"
					description="Owners can view every current and future location, open any location dashboard, and use View All Locations."
					entries={accessState.owners}
					emptyState={emptyStateCopy.owners}
					emailValue={ownerEmail}
					emailPlaceholder="Add owner email"
					submitLabel="Add Owner"
					submitting={activeMutation === 'add-owner'}
					disabled={actionsLocked}
					onEmailChange={setOwnerEmail}
					onSubmit={submitOwner}
					renderRemoveDisabledReason={() => (actionsLocked ? 'Please wait for the current change to finish.' : null)}
					onRemove={removeOwner}
					rowIsRemoving={entry => activeMutation === `remove-owner:${entry.email}`}
				/>

				<AccessSection
					title="Admin Accounts"
					description="Admins inherit all owner access and can manage locations, access roles, the status sidebar, and manual operational actions."
					entries={accessState.admins}
					emptyState={emptyStateCopy.admins}
					emailValue={adminEmail}
					emailPlaceholder="Add admin email"
					submitLabel="Add Admin"
					submitting={activeMutation === 'add-admin'}
					disabled={actionsLocked}
					onEmailChange={setAdminEmail}
					onSubmit={submitAdmin}
					renderRemoveDisabledReason={renderAdminRemoveDisabledReason}
					onRemove={removeAdmin}
					rowIsRemoving={entry => activeMutation === `remove-admin:${entry.email}`}
				/>
			</div>
		</div>
	)
}

export default AccessManagement
